import { BandSeries, SizeOption, IVariant, ColorOption, BandMaker } from '../types';
import { expectUnreachable } from './util';
import i18next from '../../i18n';
import { FiCountry } from '../contexts/LocaleContext';

export type BandWidth = '3/4"' | '1"';
export type BandWidthCm = '2 cm' | '2.5 cm';

export type MeasurementUnit = 'in' | 'cm';

export function getDefaultMeasurementUnit(country: FiCountry): MeasurementUnit {
  return country === FiCountry.CA ? 'cm' : 'in';
}

// Only show the size options that are available for the F1 band series based on inventory.
export const f1BandSizeOptions = [SizeOption.Small, SizeOption.Medium, SizeOption.Large, SizeOption.XLarge] as const;
export type F1BandSizeOption = (typeof f1BandSizeOptions)[number];

export const f3BandSizeOptions = [
  SizeOption.XSmall,
  SizeOption.Small,
  SizeOption.Medium,
  SizeOption.Large,
  SizeOption.XLarge,
] as const;
export type F3BandSizeOption = (typeof f3BandSizeOptions)[number];

export interface SizeOptionProps {
  bandSeries: BandSeries;
  sizeOption: F1BandSizeOption | F3BandSizeOption;
  bandMaker?: BandMaker;
  color?: ColorOption;
  measurementUnit?: MeasurementUnit;
}

export interface BandSizeDetails {
  dogList: string;
  sizeName: string;
  neckSize: string;
  bandWidth: BandWidth | BandWidthCm;
}

export function sortedSizeOptionsForVariants(variants: IVariant[]) {
  const sizeOptions = new Set(variants.map((variant) => variant.options.size));
  const sizeSortOrder = (sizeOption: SizeOption) => {
    if (sizeOption === SizeOption.XSmall) {
      return 0;
    } else if (sizeOption === SizeOption.Small) {
      return 1;
    } else if (sizeOption === SizeOption.Medium) {
      return 2;
    } else if (sizeOption === SizeOption.Large) {
      return 3;
    } else if (sizeOption === SizeOption.XLarge) {
      return 4;
    } else {
      expectUnreachable(sizeOption);
      throw new Error(i18next.t('unknown_size_option', { sizeOption }));
    }
  };

  return Array.from(sizeOptions).sort((a, b) => sizeSortOrder(a) - sizeSortOrder(b));
}

export function commonDetailsForSize(sizeOption: SizeOption): { dogList: string; sizeName: string } {
  if (sizeOption === SizeOption.XSmall) {
    return {
      dogList: i18next.t('dog_list_x_small'),
      sizeName: i18next.t('size_name_x_small'),
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      dogList: i18next.t('dog_list_small'),
      sizeName: i18next.t('size_name_small'),
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      dogList: i18next.t('dog_list_medium'),
      sizeName: i18next.t('size_name_medium'),
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      dogList: i18next.t('dog_list_large'),
      sizeName: i18next.t('size_name_large'),
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      dogList: i18next.t('dog_list_x_large'),
      sizeName: i18next.t('size_name_x_large'),
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF1SizeOption(sizeOption: F1BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '11.5" - 13.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '13" - 16.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '16" - 22.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '22" - 34.5"',
      bandWidth: '1"',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF3SizeOption(sizeOption: F3BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.XSmall) {
    return {
      ...commonDetails,
      neckSize: '9.5" - 11"',
      bandWidth: '3/4"',
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '10.5" - 13"',
      bandWidth: '3/4"',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '12.5" - 16.5"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '15.5" - 22"',
      bandWidth: '1"',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '20" - 31"',
      bandWidth: '1"',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForF3SizeOptionCm(sizeOption: F3BandSizeOption): BandSizeDetails {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (sizeOption === SizeOption.XSmall) {
    return {
      ...commonDetails,
      neckSize: '24 - 38 cm',
      bandWidth: '2 cm',
    };
  } else if (sizeOption === SizeOption.Small) {
    return {
      ...commonDetails,
      neckSize: '27 - 33 cm',
      bandWidth: '2 cm',
    };
  } else if (sizeOption === SizeOption.Medium) {
    return {
      ...commonDetails,
      neckSize: '32 - 42 cm',
      bandWidth: '2.5 cm',
    };
  } else if (sizeOption === SizeOption.Large) {
    return {
      ...commonDetails,
      neckSize: '39 - 56 cm',
      bandWidth: '2.5 cm',
    };
  } else if (sizeOption === SizeOption.XLarge) {
    return {
      ...commonDetails,
      neckSize: '51 - 79 cm',
      bandWidth: '2.5 cm',
    };
  } else {
    expectUnreachable(sizeOption);
    throw new Error(i18next.t('unknown_size_option', { sizeOption }));
  }
}

function sizeDetailsForMakersBands(sizeOption: SizeOption, colorOption: ColorOption): BandSizeDetails | null {
  const commonDetails = commonDetailsForSize(sizeOption);

  if (colorOption === ColorOption.ZeeDogPhantom) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.5" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 16.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '20" - 31"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.StuntPuppyGoDog || colorOption === ColorOption.StuntPuppyTealBiothane) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.75" - 12.5"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '12.5" - 15.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '15.5" - 20.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19.5" - 30"',
        bandWidth: '1"',
      };
    }
  } else if (
    colorOption === ColorOption.StuntPuppyPinesBlue ||
    colorOption === ColorOption.StuntPuppyFishtailTeal ||
    colorOption === ColorOption.StuntPuppyFloraFrolic
  ) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '10.75" - 12.5"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '12.5" - 15.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '15.5" - 20.5"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19.5" - 30"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.MimiGreenRedBiothane) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '22" - 26"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.MimiGreenPinkReflective) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 13"',
        bandWidth: '3/4"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '13" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '16" - 22"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '22" - 26"',
        bandWidth: '1"',
      };
    }
  } else if (colorOption === ColorOption.RopeHoundsMountainTop || colorOption === ColorOption.RopeHoundsBubblegumPop) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '12" - 15"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '15" - 20"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '17" - 24"',
        bandWidth: '1"',
      };
    }
  } else if (
    colorOption === ColorOption.LandsharkSupplyBlackBlue ||
    colorOption === ColorOption.LandsharkSupplyBlackRed
  ) {
    if (sizeOption === SizeOption.Small) {
      return {
        ...commonDetails,
        neckSize: '13" - 16"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Medium) {
      return {
        ...commonDetails,
        neckSize: '15" - 19"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.Large) {
      return {
        ...commonDetails,
        neckSize: '17" - 23"',
        bandWidth: '1"',
      };
    } else if (sizeOption === SizeOption.XLarge) {
      return {
        ...commonDetails,
        neckSize: '19" - 27"',
        bandWidth: '1"',
      };
    }
  }

  // For display purposes, fallback to default F3 sizing
  return null;
}

export function sizeDetailsForSizeOption(props: SizeOptionProps) {
  if (props.bandMaker !== BandMaker.Fi && props.color) {
    return sizeDetailsForMakersBands(props.sizeOption, props.color);
  }

  if (props.bandSeries === BandSeries.F1) {
    return sizeDetailsForF1SizeOption(props.sizeOption as F1BandSizeOption);
  } else if (props.bandSeries === BandSeries.F3) {
    if (props.measurementUnit === 'cm') {
      return sizeDetailsForF3SizeOptionCm(props.sizeOption);
    }
    return sizeDetailsForF3SizeOption(props.sizeOption);
  } else {
    expectUnreachable(props.bandSeries);
    throw new Error(i18next.t('unknown_band_series', { bandSeries: props.bandSeries }));
  }
}
